import AuthAPI from '../api/auth';
import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';
import DashboardAudioNotificationHelper from './AudioAlerts/DashboardAudioNotificationHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { useImpersonation } from 'dashboard/composables/useImpersonation';
import { useCallsStore } from 'dashboard/stores/calls';

const { isImpersonating } = useImpersonation();

class ActionCableConnector extends BaseActionCableConnector {
  constructor(app, pubsubToken) {
    const { websocketURL = '' } = window.chatwootConfig || {};
    super(app, pubsubToken, websocketURL);
    this.CancelTyping = [];
    this.events = {
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'conversation.created': this.onConversationCreated,
      'conversation.status_changed': this.onStatusChange,
      'user:logout': this.onLogout,
      'page:reload': this.onReload,
      'assignee.changed': this.onAssigneeChanged,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.contact_changed': this.onConversationContactChange,
      'presence.update': this.onPresenceUpdate,
      'contact.deleted': this.onContactDelete,
      'contact.updated': this.onContactUpdate,
      'conversation.mentioned': this.onConversationMentioned,
      'notification.created': this.onNotificationCreated,
      'notification.deleted': this.onNotificationDeleted,
      'notification.updated': this.onNotificationUpdated,
      'conversation.read': this.onConversationRead,
      'conversation.updated': this.onConversationUpdated,
      'account.cache_invalidated': this.onCacheInvalidate,
      'copilot.message.created': this.onCopilotMessageCreated,
      'internal_message.created': this.onInternalMessageCreated,
      'crm_task.overdue': this.onCrmTaskOverdue,
      'admin_broadcast.created': this.onAdminBroadcast,
      'voice.inbound_call': this.onVoiceInboundCall,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT);
  };

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_DISCONNECT);
  };

  isAValidEvent = data => {
    return this.app.$store.getters.getCurrentAccountId === data.account_id;
  };

  onMessageUpdated = data => {
    this.app.$store.dispatch('updateMessage', data);
  };

  onPresenceUpdate = data => {
    if (isImpersonating.value) return;
    this.app.$store.dispatch('contacts/updatePresence', data.contacts);
    this.app.$store.dispatch('agents/updatePresence', data.users);
    this.app.$store.dispatch('setCurrentUserAvailability', data.users);
  };

  onConversationContactChange = payload => {
    const { meta = {}, id: conversationId } = payload;
    const { sender } = meta || {};
    if (conversationId) {
      this.app.$store.dispatch('updateConversationContact', {
        conversationId,
        ...sender,
      });
    }
  };

  onAssigneeChanged = payload => {
    const { id } = payload;
    if (id) {
      const allConversations =
        this.app.$store.getters.getAllConversations || [];
      const exists = allConversations.some(c => c.id === id);
      if (exists) {
        this.app.$store.dispatch('updateConversation', payload);
      } else {
        this.app.$store.dispatch('addConversation', payload);
      }
    }
    this.fetchConversationStats();
  };

  onConversationCreated = data => {
    this.app.$store.dispatch('addConversation', data);
    this.fetchConversationStats();
  };

  onConversationRead = data => {
    this.app.$store.dispatch('updateConversation', data);
  };

  // eslint-disable-next-line class-methods-use-this
  onLogout = () => AuthAPI.logout();

  onMessageCreated = data => {
    const {
      conversation: { last_activity_at: lastActivityAt },
      conversation_id: conversationId,
    } = data;
    DashboardAudioNotificationHelper.onNewMessage(data);
    this.app.$store.dispatch('addMessage', data);
    this.app.$store.dispatch('updateConversationLastActivity', {
      lastActivityAt,
      conversationId,
    });
  };

  // eslint-disable-next-line class-methods-use-this
  onReload = () => window.location.reload();

  onStatusChange = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onConversationUpdated = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onTypingOn = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/create', {
      conversationId,
      user,
    });
    this.initTimer({ conversation, user });
  };

  onTypingOff = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/destroy', {
      conversationId,
      user,
    });
  };

  onConversationMentioned = data => {
    this.app.$store.dispatch('addMentions', data);
  };

  clearTimer = conversationId => {
    const timerEvent = this.CancelTyping[conversationId];

    if (timerEvent) {
      clearTimeout(timerEvent);
      this.CancelTyping[conversationId] = null;
    }
  };

  initTimer = ({ conversation, user }) => {
    const conversationId = conversation.id;
    // Turn off typing automatically after 30 seconds
    this.CancelTyping[conversationId] = setTimeout(() => {
      this.onTypingOff({ conversation, user });
    }, 30000);
  };

  // eslint-disable-next-line class-methods-use-this
  fetchConversationStats = () => {
    emitter.emit('fetch_conversation_stats');
  };

  onContactDelete = data => {
    this.app.$store.dispatch(
      'contacts/deleteContactThroughConversations',
      data.id
    );
    this.fetchConversationStats();
  };

  onContactUpdate = data => {
    this.app.$store.dispatch('contacts/updateContact', data);
  };

  onNotificationCreated = data => {
    this.app.$store.dispatch('notifications/addNotification', data);
  };

  onNotificationDeleted = data => {
    this.app.$store.dispatch('notifications/deleteNotification', data);
  };

  onNotificationUpdated = data => {
    this.app.$store.dispatch('notifications/updateNotification', data);
  };

  onCopilotMessageCreated = data => {
    this.app.$store.dispatch('copilotMessages/upsert', data);
  };

  onInternalMessageCreated = data => {
    this.app.$store.dispatch('internalChat/addMessage', data);

    const currentUser = this.app.$store.getters.getCurrentUser;
    if (!currentUser || data.user_id === currentUser.id) return;

    // Emit for bubble tooltip
    emitter.emit('internalChatNewMessage', { sender_name: data.sender_name, content: data.content });

    const isNudge = data.content === '🔔 CHAMOU SUA ATENÇÃO!';

    // Play audio - nudge plays 3 rapid dings
    try {
      const playDing = (delay = 0) => {
        setTimeout(() => {
          const audio = new Audio('/audio/dashboard/ding.mp3');
          audio.play();
        }, delay);
      };
      playDing(0);
      if (isNudge) {
        playDing(400);
        playDing(800);
      }
    } catch (e) {
      // silent
    }

    // Browser notification
    if (Notification.permission === 'granted') {
      const title = isNudge
        ? `🔔 ${data.sender_name} chamou sua atenção!`
        : `💬 ${data.sender_name}`;
      const body = isNudge
        ? 'Clique para responder agora'
        : data.content?.substring(0, 100);
      const notification = new Notification(title, {
        body,
        icon: data.sender_avatar || '/brand-assets/logo.svg',
        tag: `internal-chat-${data.internal_conversation_id}`,
        requireInteraction: isNudge,
      });
      notification.onclick = () => {
        window.focus();
        this.app.$store.dispatch('internalChat/openPanel');
        this.app.$store.dispatch('internalChat/setActiveConversation', data.internal_conversation_id);
        notification.close();
      };
      if (!isNudge) setTimeout(() => notification.close(), 5000);
    } else if (Notification.permission !== 'denied') {
      Notification.requestPermission();
    }

    // Auto-open panel on nudge
    if (isNudge) {
      this.app.$store.dispatch('internalChat/openPanel');
      this.app.$store.dispatch('internalChat/setActiveConversation', data.internal_conversation_id);
    }
  };

  onCacheInvalidate = data => {
    const keys = data.cache_keys;
    this.app.$store.dispatch('labels/revalidate', { newKey: keys.label });
    this.app.$store.dispatch('inboxes/revalidate', { newKey: keys.inbox });
    this.app.$store.dispatch('teams/revalidate', { newKey: keys.team });
  };

  onCrmTaskOverdue = data => {
    const { task, deal } = data;
    const storageKey = 'cw_crm_overdue_alerts';
    const snoozeKey = 'cw_crm_overdue_snoozed';

    // Check if this task is currently snoozed
    try {
      const snoozed = JSON.parse(localStorage.getItem(snoozeKey) || '{}');
      const snoozedUntil = snoozed[task.id];
      if (snoozedUntil && Date.now() < snoozedUntil) return;
      // Clean expired snooze
      if (snoozedUntil) {
        delete snoozed[task.id];
        localStorage.setItem(snoozeKey, JSON.stringify(snoozed));
      }
    } catch (e) {
      // silent
    }

    // Store in localStorage for persistence across refresh
    const alertData = {
      account_id: data.account_id,
      task: { id: task.id, title: task.title, category: task.category, due_at: task.due_at },
      deal: { id: deal.id, name: deal.name, pipeline: deal.pipeline, stage: deal.stage },
    };
    try {
      const stored = JSON.parse(localStorage.getItem(storageKey) || '{}');
      stored[task.id] = alertData;
      localStorage.setItem(storageKey, JSON.stringify(stored));
    } catch (e) {
      // silent
    }

    // Play alert sound
    try {
      const audio = new Audio('/audio/dashboard/ding.mp3');
      audio.play();
      setTimeout(() => {
        const audio2 = new Audio('/audio/dashboard/ding.mp3');
        audio2.play();
      }, 500);
    } catch (e) {
      // silent
    }

    const message = `⚠️ Tarefa atrasada: "${task.title}" no negócio "${deal.name}"`;
    const dealUrl = `/app/accounts/${data.account_id}/crm/deals?deal_id=${deal.id}`;

    emitter.emit('crmTaskOverdue', {
      taskId: task.id,
      message,
      dealUrl,
    });

    // Browser notification
    if (Notification.permission === 'granted') {
      const notification = new Notification('⚠️ Tarefa CRM Atrasada', {
        body: `"${task.title}" no negócio "${deal.name}"`,
        icon: '/brand-assets/logo.svg',
        tag: `crm-task-overdue-${task.id}`,
        requireInteraction: true,
      });
      notification.onclick = () => {
        window.focus();
        window.location.href = dealUrl;
        notification.close();
      };
    } else if (Notification.permission !== 'denied') {
      Notification.requestPermission();
    }
  };

  onVoiceInboundCall = data => {
    const callsStore = useCallsStore();

    callsStore.addCall({
      callSid: data.call_sid,
      conversationId: data.conversation_id,
      inboxId: data.inbox_id,
      callDirection: 'inbound',
      fromNumber: data.from_number,
    });

    // Play ringtone
    try {
      const audio = new Audio('/audio/dashboard/ting.mp3');
      audio.play().catch(() => {
        const fallback = new Audio('/audio/dashboard/ding.mp3');
        fallback.play();
      });
    } catch (e) {
      // silent
    }

    // Browser notification
    if (Notification.permission === 'granted') {
      const notification = new Notification('📞 Chamada Recebida', {
        body: `Ligação de ${data.from_number || 'número desconhecido'}`,
        icon: '/brand-assets/logo.svg',
        tag: `voice-inbound-${data.call_sid}`,
        requireInteraction: true,
      });
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    }
  };

  onAdminBroadcast = data => {
    // Play urgent alert sound
    try {
      const playDing = (delay = 0) => {
        setTimeout(() => {
          const audio = new Audio('/audio/dashboard/ding.mp3');
          audio.play();
        }, delay);
      };
      playDing(0);
      playDing(300);
      playDing(600);
    } catch (e) {
      // silent
    }

    // Emit bus event for AdminBroadcastAlert component
    emitter.emit('adminBroadcast', data);

    // Browser notification
    if (Notification.permission === 'granted') {
      const notification = new Notification(
        `📢 Alerta de ${data.sender_name}`,
        {
          body: data.message,
          icon: data.sender_avatar || '/brand-assets/logo.svg',
          tag: `admin-broadcast-${Date.now()}`,
          requireInteraction: true,
        }
      );
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    }
  };
}

export default {
  init(store, pubsubToken) {
    return new ActionCableConnector({ $store: store }, pubsubToken);
  },
};
